// swish'd — food flavor + pairing rules for re-ranking
// Food vector axes: fatty, salty, spicy, umami, sweet, acidic, bitter
// Drink vector axes: sweet, bitter, acidic, smoky, herbal, boozy, dry, fruity
//
// Pairing fit between a food vector and a drink vector is a hand-coded blend
// of established rules — not AI-generated. The food vector itself is AI-generated
// and cached.

// Compute a "pairing fit" score in [0,1]. Higher = better pairing.
// Rules encoded:
//  - Fatty foods reward acidic + tannic-ish (bitter) drinks (cuts fat)
//  - Salty foods reward acidic and sweet (balances salt)
//  - Spicy foods reward sweet + fruity, penalize boozy (alcohol amplifies heat)
//  - Umami (savory, mushroomy, aged) rewards earthy/dry/herbal
//  - Sweet foods reward sweet drinks (matched sweetness) and penalize dry/bitter
//  - Acidic foods reward acidic drinks (matched acidity)
//  - Bitter foods reward bitter drinks (matched), or sweet (contrast)
export function pairingFit(foodVec, drinkVec) {
  if (!foodVec || !drinkVec) return 0.5;
  const f = (k) => Number(foodVec[k]) || 0;
  const d = (k) => Number(drinkVec[k]) || 0;

  let score = 0.5; // start neutral

  // Fatty: acidic and bitter cleanse the palate. Strong rule.
  score += 0.20 * f('fatty') * d('acidic');
  score += 0.10 * f('fatty') * d('bitter');

  // Salty: acidic balances, sweet contrasts.
  score += 0.10 * f('salty') * d('acidic');
  score += 0.05 * f('salty') * d('sweet');

  // Spicy: sweet/fruity tames heat, boozy amplifies it.
  score += 0.15 * f('spicy') * d('sweet');
  score += 0.10 * f('spicy') * d('fruity');
  score -= 0.15 * f('spicy') * d('boozy');

  // Umami: rewards herbal, dry, earthy (smoky).
  score += 0.10 * f('umami') * d('herbal');
  score += 0.10 * f('umami') * d('dry');
  score += 0.05 * f('umami') * d('smoky');

  // Sweet food: matched sweetness (rule: drink should be at least as sweet as food).
  // Penalize dry/bitter against sweet desserts.
  score += 0.15 * f('sweet') * d('sweet');
  score -= 0.15 * f('sweet') * d('dry');
  score -= 0.10 * f('sweet') * d('bitter');

  // Acidic food: matched acidity.
  score += 0.10 * f('acidic') * d('acidic');

  // Bitter food (greens, radicchio): matched or sweet contrast.
  score += 0.05 * f('bitter') * d('bitter');
  score += 0.05 * f('bitter') * d('sweet');

  // Clamp
  if (score < 0) score = 0;
  if (score > 1) score = 1;
  return score;
}

// Normalize a food name for cache keying. Mirrors normalizeDrinkKey.
export function normalizeFoodKey(name) {
  return (name || '')
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9 ]+/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

// Default fallback vector if AI fails or no key. Neutral across the board.
export const NEUTRAL_FOOD_VECTOR = {
  fatty: 0.5, salty: 0.4, spicy: 0.2, umami: 0.4, sweet: 0.2, acidic: 0.3, bitter: 0.2,
};
